<template>
  <div>
    <div class="mb-3" v-if="!isTableBusy">
      <small class="text-muted">
        {{ total.toLocaleString() }} {{ mode === 'partial' ? 'partial extraction' : 'failed' }} application(s)
      </small>
    </div>

    <!-- Table -->
    <div class="table-responsive">
      <BTable
        :items="applications"
        :fields="fields"
        :busy="isTableBusy"
        hover
        show-empty
        :empty-text="mode === 'partial' ? 'No partial extraction applications' : 'No failed scoring applications'"
      >
        <template #table-busy>
          <div class="text-center my-4">
            <BSpinner class="align-middle"></BSpinner>
            <span class="ml-2">Loading...</span>
          </div>
        </template>

        <template #cell(candidate)="{ item }">
          <router-link
            :to="{ name: 'head-hunting-candidate', params: { candidateProfileId: item.candidate_id } }"
            class="font-weight-medium"
          >
            {{ item.candidate_name }}
          </router-link>
          <div class="small text-muted">{{ item.candidate_email }}</div>
        </template>

        <template #cell(offer)="{ item }">
          <span v-if="item.offer_title">{{ item.offer_title }}</span>
          <span v-else class="text-muted">-</span>
          <div v-if="item.company_name" class="small text-muted">{{ item.company_name }}</div>
        </template>

        <template #cell(mime_type)="{ item }">
          <small :title="item.mime_type">{{ shortMime(item.mime_type) }}</small>
        </template>

        <template #cell(extraction_status)="{ item }">
          <BBadge :variant="getExtractionVariant(item.extraction_status)">
            {{ item.extraction_status || 'none' }}
          </BBadge>
        </template>

        <template #cell(error)="{ item }">
          <span
            v-if="item.error"
            class="text-danger small error-text"
            :title="item.error"
          >{{ truncate(item.error, 60) }}</span>
          <span v-else class="text-muted">-</span>
        </template>

        <template #cell(text_length)="{ item }">
          <span class="small">{{ item.text_length }} chars</span>
        </template>

        <template #cell(created_at)="{ item }">
          <small>{{ formatDate(item.created_at) }}</small>
        </template>

        <template #cell(actions)="{ item }">
          <BButton
            size="sm"
            :variant="isRetryQueued(item) ? 'outline-success' : 'outline-warning'"
            @click="retrySingle(item)"
            :disabled="retryingId === item.id || isRetryQueued(item)"
            :title="isRetryQueued(item) ? 'Queued' : 'Retry Scoring'"
          >
            <i class="fa" :class="getRetryIcon(item)"></i>
            <span v-if="isRetryQueued(item)" class="ml-1">Running</span>
          </BButton>
        </template>
      </BTable>

      <BPagination
        v-if="total > perPage"
        :disabled="isTableBusy"
        v-model="currentPage"
        @input="fetch"
        :total-rows="total"
        :per-page="perPage"
        align="center"
      ></BPagination>
    </div>
  </div>
</template>

<script>
import {
  BTable,
  BSpinner,
  BPagination,
  BBadge,
  BButton,
} from "bootstrap-vue";

export default {
  components: { BTable, BSpinner, BPagination, BBadge, BButton },
  props: {
    mode: {
      type: String,
      default: "failed",
      validator: function (v) { return ["failed", "partial"].indexOf(v) !== -1; },
    },
  },
  data() {
    return {
      applications: [],
      currentPage: 1,
      perPage: 25,
      total: 0,
      isTableBusy: true,
      retryingId: null,
      retryQueuedIds: [],
    };
  },
  computed: {
    fields() {
      var base = [
        { key: "candidate", label: "Candidate" },
        { key: "offer", label: "Offer" },
        { key: "mime_type", label: "Type", thStyle: { width: "80px" } },
        { key: "extraction_status", label: "Extraction", thStyle: { width: "110px" } },
      ];
      if (this.mode === "partial") {
        base.push({ key: "text_length", label: "Text", thStyle: { width: "100px" } });
      } else {
        base.push({ key: "error", label: "Error", thStyle: { width: "200px" } });
      }
      base.push({ key: "created_at", label: "Applied", thStyle: { width: "100px" } });
      base.push({ key: "actions", label: "", thStyle: { width: "100px" }, class: "text-right" });
      return base;
    },
  },
  created() {
    this.fetch();
  },
  methods: {
    fetch() {
      this.isTableBusy = true;
      this.$http
        .get("/enroll_admin/head-hunting/scoring-queue", {
          params: {
            page: this.currentPage,
            per_page: this.perPage,
            status: this.mode,
          },
        })
        .then(function (res) {
          this.applications = res.data.data || [];
          this.total = res.data.total;
          this.currentPage = res.data.current_page;
        }.bind(this))
        .catch(function () {
          this.applications = [];
        }.bind(this))
        .finally(function () {
          this.isTableBusy = false;
        }.bind(this));
    },
    retrySingle(item) {
      if (this.retryingId || this.isRetryQueued(item)) return;
      this.retryingId = item.id;
      this.$http
        .post("/enroll_admin/head-hunting/applications/" + item.id + "/retry-scoring", {
          re_extract: true,
        })
        .then(function () {
          this.retryQueuedIds.push(item.id);
        }.bind(this))
        .catch(function (err) {
          if (err.response && err.response.status === 409) {
            this.retryQueuedIds.push(item.id);
          } else {
            this.$notify({ type: "danger", title: "Error", message: "Failed to queue retry" });
          }
        }.bind(this))
        .finally(function () {
          this.retryingId = null;
        }.bind(this));
    },
    isRetryQueued(item) {
      return item.retry_queued || this.retryQueuedIds.indexOf(item.id) !== -1;
    },
    getRetryIcon(item) {
      if (this.retryingId === item.id) return "fa-spinner fa-spin";
      if (this.isRetryQueued(item)) return "fa-check";
      return "fa-redo";
    },
    getExtractionVariant(status) {
      var variants = {
        pending: "warning",
        completed: "success",
        failed: "danger",
        encrypted: "dark",
      };
      return variants[status] || "secondary";
    },
    formatDate(dateStr) {
      if (!dateStr) return "-";
      return new Date(dateStr).toLocaleDateString();
    },
    truncate(str, len) {
      if (!str) return "";
      return str.length > len ? str.substring(0, len) + "..." : str;
    },
    shortMime(mime) {
      if (!mime) return "-";
      var map = {
        "application/pdf": "PDF",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "DOCX",
        "application/msword": "DOC",
        "application/rtf": "RTF",
        "text/plain": "TXT",
        "image/png": "PNG",
        "image/jpeg": "JPEG",
      };
      return map[mime] || mime.split("/").pop();
    },
  },
};
</script>

<style scoped>
.font-weight-medium {
  font-weight: 500;
}
.error-text {
  cursor: help;
}
</style>
